import React from "react";
import ReactDOM from "react-dom/client";
import { StyledEngineProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "react-oidc-context";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import { AppThemeProvider } from "./ThemeContext";
import config from "./config.json";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data will be considered "fresh" for 5 minutes, preventing re-fetches. 
      staleTime: 1000 * 60 * 5, // Using default staleTime: 0. 
      // Prevents re-fetching when you click back onto the browser window.
      refetchOnWindowFocus: false, 
    },
  },
});

const cognitoConfig = config.cognito;

const cognitoOidcConfig = {
  authority: `https://${cognitoConfig.hostedUIDomain}`,
  client_id: cognitoConfig.clientId, // Your Cognito App Client ID
  redirect_uri: cognitoConfig.redirectUri, // Must be in Allowed callback URLs
  post_logout_redirect_uri: cognitoConfig.postLogoutRedirectUri, // Must be in Allowed sign-out URLs
  response_type: "code", // Standard for authorization code flow
  scope: cognitoConfig.scope, // Requested scopes (ensure these are enabled in your App Client)
  // Enable automatic silent token renewal
  automaticSilentRenew: true,
  metadata: { // Explicitly define OIDC endpoints
    issuer: `https://cognito-idp.${cognitoConfig.userPoolId.split('_')[0]}.amazonaws.com/${cognitoConfig.userPoolId}`,
    authorization_endpoint: `https://${cognitoConfig.hostedUIDomain}/oauth2/authorize`,
    token_endpoint: `https://${cognitoConfig.hostedUIDomain}/oauth2/token`,
    userinfo_endpoint: `https://${cognitoConfig.hostedUIDomain}/oauth2/userinfo`,
    jwks_uri: `https://cognito-idp.${cognitoConfig.userPoolId.split('_')[0]}.amazonaws.com/${cognitoConfig.userPoolId}/.well-known/jwks.json`,
    end_session_endpoint: `https://${cognitoConfig.hostedUIDomain}/logout`, // Crucial for correct logout
  },
  loadUserInfo: false, // Let's rely on the ID token for user info for now
};

const CreateBase = () => {
  return (
    <StyledEngineProvider>
      <AppThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AppThemeProvider>
    </StyledEngineProvider>
  );
};

const CreateApp = () => {
  return (
    <AuthProvider {...cognitoOidcConfig}>
      <CreateBase />
    </AuthProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(<CreateApp />);
